import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PropertyCard from '../components/PropertyCard';
import SearchFilters from '../components/SearchFilters';
import { FloatingOrnaments, UzbekOrnament } from '../components/UzbekOrnament';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { features } from '../data/mock';
import { districtsApi, propertiesApi } from '../services/api';
import { ArrowRight, Shield, Users, Lock, Home, MapPin, ChevronRight, Star } from 'lucide-react';

const LandingPage = () => {
  const { t, getLocalizedText, language } = useLanguage();
  const { user, login } = useAuth();
  const [districts, setDistricts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    district: 'all',
    priceType: 'all',
    minPrice: 0,
    maxPrice: 1000000,
    bedrooms: 'any',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [districtsData, propertiesData] = await Promise.all([
          districtsApi.getAll(),
          propertiesApi.getAll()
        ]);
        console.log('Districts:', districtsData);
        console.log('Properties:', propertiesData);
        setDistricts(Array.isArray(districtsData) ? districtsData : []);
        setProperties(Array.isArray(propertiesData) ? propertiesData : []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const featureIcons = { Shield, Users, Lock, Home };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50">
      <FloatingOrnaments />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
            <img
              src="https://images.unsplash.com/photo-1677156811762-842312963ecd"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            {/* Ornament */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-1 bg-gradient-to-r from-blue-600 to-blue-400 rounded-full" />
              <span className="text-blue-600 font-medium">🇺🇿 Uzbekistan's #1 Real Estate Platform</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              {t('tagline')}
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              {t('heroSubtitle')}
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <Link to="/map">
                <Button className="h-14 px-8 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-lg shadow-xl shadow-blue-200 gap-2">
                  <MapPin className="w-5 h-5" />
                  {t('exploreDistricts')}
                </Button>
              </Link>
              {!user && (
                <Button
                  variant="outline"
                  onClick={login}
                  className="h-14 px-8 border-2 border-gray-300 text-gray-700 rounded-xl text-lg gap-2 hover:border-blue-300 hover:text-blue-600"
                >
                  {t('login')}
                </Button>
              )}
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-8">
              <div>
                <p className="text-3xl font-bold text-blue-600">500+</p>
                <p className="text-gray-500">{t('properties')}</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-blue-600">11</p>
                <p className="text-gray-500">Tashkent Districts</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-blue-600">1000+</p>
                <p className="text-gray-500">{t('trustedPartners')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-8 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4">
          <SearchFilters filters={filters} setFilters={setFilters} onSearch={() => {}} />
        </div>
      </section>

      {/* Districts Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                {t('exploreDistricts')}
              </h2>
              <p className="text-gray-600">Discover properties across Tashkent's neighborhoods</p>
            </div>
            <Link to="/map">
              <Button variant="ghost" className="gap-2 text-blue-600 hover:text-blue-700">
                View Map <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.isArray(districts) && districts.slice(0, 8).map((district, index) => (
              <Link
                key={district.district_id}
                to={`/map?district=${district.district_id}`}
                className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 p-6 h-36 flex flex-col justify-end transition-transform hover:scale-105 hover:shadow-2xl hover:shadow-blue-200"
              >
                <div className="absolute top-2 right-2 opacity-20 group-hover:opacity-30 transition-opacity">
                  <UzbekOrnament size={60} color="white" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-1">
                  {getLocalizedText(district.name)}
                </h3>
                <p className="text-blue-200 text-sm">
                  {district.property_count} {t('properties')}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                {t('featuredProperties')}
              </h2>
              <p className="text-gray-600">Hand-picked properties for you</p>
            </div>
            <Link to="/properties">
              <Button variant="ghost" className="gap-2 text-blue-600 hover:text-blue-700">
                View All <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.isArray(properties) && properties.slice(0, 6).map((property) => (
              <PropertyCard key={property.property_id || property.id} property={property} />
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-blue-50 to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('whyChooseUs')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We provide the best real estate experience in Uzbekistan
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = featureIcons[feature.icon] || Shield;
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-white rounded-2xl overflow-hidden group">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-2xl flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                      <Icon className="w-8 h-8 text-blue-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-semibold text-lg text-gray-900 mb-2">
                      {getLocalizedText(feature.title)}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {getLocalizedText(feature.description)}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-blue-700 via-blue-800 to-blue-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10"><UzbekOrnament size={150} color="white" /></div>
          <div className="absolute bottom-10 right-10 rotate-180"><UzbekOrnament size={150} color="white" /></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Ready to Find Your Perfect Home?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of families who found their dream property with Uz-Space
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/map">
              <Button className="h-14 px-8 bg-white text-blue-700 hover:bg-blue-50 rounded-xl text-lg shadow-xl gap-2">
                <MapPin className="w-5 h-5" />
                Explore Map
              </Button>
            </Link>
            {!user && (
              <Button
                variant="outline"
                onClick={login}
                className="h-14 px-8 border-2 border-white/30 text-white hover:bg-white/10 rounded-xl text-lg gap-2"
              >
                Get Started
              </Button>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
